import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { getActivityFeed, getActivities } from '../api/activities';
import { formatActivityName, fetchDefaultAvatarSvg } from '../utils';
import { getAvatar } from '../api/avatars';
import { getAccount } from '../api/auth';
import { getParticipatingChallenges } from '../api/challenges';
import { getReactionsBatch, getAllowedEmojis } from '../api/reactions';
import { getMyStreak } from '../api/users';
import Avatar from '../components/Avatar';
import ReactionBar from '../components/ReactionBar';
import NotificationBell from '../components/NotificationBell';
import '../styles/Dashboard.css';

const PAGE_SIZE = 10;

const DashboardPage = () => {
    const history = useHistory();
    const [activities, setActivities] = useState([]);
    const [avatars, setAvatars] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
    const [userAvatar, setUserAvatar] = useState('');
    const [username, setUsername] = useState('');
    const [activeChallenges, setActiveChallenges] = useState([]);
    const [reactions, setReactions] = useState({});
    const [currentUserId, setCurrentUserId] = useState(null);
    const [allowedEmojis, setAllowedEmojis] = useState([]);
    const [streak, setStreak] = useState(null);
    const [weeklyStats, setWeeklyStats] = useState({ wtdPoints: 0, wtdCount: 0, lwPoints: 0 });
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);

    const fetchUserData = async () => {
        try {
            const data = await getAccount();
            setUsername(data.user.username);
            setCurrentUserId(data.user.auth_id);
            try {
                const emojis = await getAllowedEmojis();
                setAllowedEmojis(emojis);
            } catch (e) {
                console.error('Failed to fetch allowed emojis:', e);
            }
            if (data.user.avatar_id) {
                const avatarData = await getAvatar(data.user.avatar_id);
                setUserAvatar(avatarData.avatar.svg_data);
            } else {
                setUserAvatar(await fetchDefaultAvatarSvg());
            }
        } catch (err) {
            console.error('Failed to fetch user data:', err);
            try {
                setUserAvatar(await fetchDefaultAvatarSvg());
            } catch (fallbackErr) {
                console.error('Failed to load default avatar:', fallbackErr);
            }
        }
    };

    const fetchStreak = async () => {
        try {
            const s = await getMyStreak();
            setStreak(s);
        } catch (err) {
            console.error('Failed to fetch streak:', err);
        }
    };

    const fetchWeeklyStats = async () => {
        // Rolling 7-day windows matching the profile weekly chart
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - 6);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        const toISO = d => d.toISOString().split('T')[0];
        try {
            const [thisWeek, lastWeek] = await Promise.all([
                getActivities(toISO(thisWeekStart), toISO(today)),
                getActivities(toISO(lastWeekStart), toISO(lastWeekEnd)),
            ]);
            setWeeklyStats({
                wtdPoints: Math.round(thisWeek.reduce((s, a) => s + (a.points || 0), 0) * 100) / 100,
                wtdCount: thisWeek.length,
                lwPoints: Math.round(lastWeek.reduce((s, a) => s + (a.points || 0), 0) * 100) / 100,
            });
        } catch (err) {
            console.error('Failed to fetch weekly stats:', err);
        }
    };

    const fetchAvatarsForActivities = async (newActivities, existingAvatars) => {
        const uniqueAvatarIds = [
            ...new Set(
                newActivities
                    .map(a => a.avatar_id)
                    .filter(id => id && !existingAvatars[id])
            ),
        ];
        const avatarData = { ...existingAvatars };
        for (const avatarId of uniqueAvatarIds) {
            try {
                const result = await getAvatar(avatarId);
                avatarData[avatarId] = result.avatar.svg_data;
            } catch (err) {
                console.error(`Failed to load avatar ${avatarId}:`, err);
                try {
                    avatarData[avatarId] = await fetchDefaultAvatarSvg();
                } catch (fallbackErr) {
                    console.error('Failed to load default avatar:', fallbackErr);
                }
            }
        }
        return avatarData;
    };

    const fetchChallenges = async () => {
        try {
            const data = await getParticipatingChallenges();
            const active = (Array.isArray(data) ? data : []).filter(
                c => c.computed_status === 'active' || c.computed_status === 'upcoming'
            );
            setActiveChallenges(active);
        } catch (err) {
            console.error('Failed to fetch challenges:', err);
        }
    };

    const fetchFeed = useCallback(async (offset = 0, append = false) => {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);
        setError(null);
        try {
            const data = await getActivityFeed(offset, PAGE_SIZE);
            const newActivities = data.activities;
            setHasMore(data.has_more);

            const currentActivities = append
                ? prev => [...prev, ...newActivities]
                : () => newActivities;

            setActivities(prev => {
                const updated = append ? [...prev, ...newActivities] : newActivities;
                // Deduplicate by id to avoid React key collisions
                const seen = new Set();
                return updated.filter(a => {
                    if (!a.id || !seen.has(a.id)) {
                        if (a.id) seen.add(a.id);
                        return true;
                    }
                    return false;
                });
            });

            setAvatars(prev => {
                fetchAvatarsForActivities(newActivities, prev).then(setAvatars);
                return prev;
            });

            // Fetch reactions for the new activities
            const activityIds = newActivities.map(a => a.id).filter(Boolean);
            if (activityIds.length > 0) {
                try {
                    const reactionsData = await getReactionsBatch(activityIds);
                    setReactions(prev => ({ ...prev, ...reactionsData.reactions }));
                } catch (err) {
                    console.error('Failed to fetch reactions:', err);
                }
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch activity feed');
        }
        setLoading(false);
        setLoadingMore(false);
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
                    setActivities(prev => {
                        fetchFeed(prev.length, true);
                        return prev;
                    });
                }
            },
            { threshold: 0.1 }
        );

        if (sentinelRef.current) {
            observerRef.current.observe(sentinelRef.current);
        }

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [hasMore, loading, loadingMore, fetchFeed]);

    // Calculate statistics (own activities only)
    const calculateStats = () => {
        const ownActivities = activities.filter(a => a.is_own);

        // Week boundaries (Monday-based)
        const now = new Date();
        const day = now.getDay(); // 0=Sun
        const diffToMonday = day === 0 ? 6 : day - 1;
        const weekStart = new Date(now);
        weekStart.setHours(0, 0, 0, 0);
        weekStart.setDate(now.getDate() - diffToMonday);

        const lastWeekStart = new Date(weekStart);
        lastWeekStart.setDate(weekStart.getDate() - 7);

        const wtd = ownActivities.filter(a => new Date(a.date + 'T00:00') >= weekStart);
        const lw = ownActivities.filter(a => {
            const d = new Date(a.date + 'T00:00');
            return d >= lastWeekStart && d < weekStart;
        });

        const wtdPoints = Math.round(wtd.reduce((s, a) => s + (a.points || 0), 0) * 100) / 100;
        const wtdCount = wtd.length;
        const lwPoints = Math.round(lw.reduce((s, a) => s + (a.points || 0), 0) * 100) / 100;

        const activityTypes = activities.reduce((types, a) => {
            types[a.activity] = (types[a.activity] || 0) + 1;
            return types;
        }, {});

        // Catalog display names keyed by activity id.
        const activityNames = activities.reduce((names, a) => {
            if (a.activity_name && !names[a.activity]) names[a.activity] = a.activity_name;
            return names;
        }, {});

        return { wtdPoints, wtdCount, lwPoints, activityTypes, activityNames };
    };

    // Filter activities by selected activity types
    const getFilteredActivities = () => {
        if (selectedActivityTypes.length === 0) return activities;
        return activities.filter(a => selectedActivityTypes.includes(a.activity));
    };

    const handleActivityClick = (activityId) => {
        history.push(`/activity/${activityId}`);
    };

    const openUserProfile = (event, usernameValue) => {
        event.stopPropagation();
        if (usernameValue) history.push(`/user/${usernameValue}`);
    };

    const handleReactionChange = async (activityId) => {
        try {
            const data = await getReactionsBatch([activityId]);
            setReactions(prev => ({
                ...prev,
                [activityId]: data.reactions[activityId] || [],
            }));
        } catch (err) {
            console.error('Failed to refresh reactions:', err);
        }
    };

    const handleActivityTypeClick = (activityType) => {
        if (selectedActivityTypes.includes(activityType)) {
            setSelectedActivityTypes(selectedActivityTypes.filter(t => t !== activityType));
        } else {
            setSelectedActivityTypes([...selectedActivityTypes, activityType]);
        }
    };

    const clearAllFilters = () => {
        setSelectedActivityTypes([]);
    };

    const stats = calculateStats();
    const filteredActivities = getFilteredActivities();

    useEffect(() => {
        fetchFeed(0, false);
        fetchUserData();
        fetchChallenges();
        fetchStreak();
        fetchWeeklyStats();

        const handleAvatarUpdate = (event) => {
            if (event.detail && event.detail.avatar) {
                setUserAvatar(event.detail.avatar);
            }
        };
        window.addEventListener('avatarUpdated', handleAvatarUpdate);
        return () => window.removeEventListener('avatarUpdated', handleAvatarUpdate);
        // eslint-disable-next-line
    }, []);

    return (
        <div className="db-page">
            <div className="db-container">
                <div className="db-header">
                    <h2>Home</h2>
                    <div className="db-header-actions">
                        <NotificationBell />
                    </div>
                </div>

                <div className="db-summary">
                    <div className="db-summary-identity">
                        {userAvatar && (
                            <div className="db-avatar-ring">
                                <Avatar
                                    svgContent={userAvatar}
                                    alt="Your avatar"
                                    className="db-avatar"
                                />
                            </div>
                        )}
                        <div className="db-summary-text">
                            <h3>Welcome back{username ? `, ${username}` : ''}</h3>
                            <p>You and your friends</p>
                        </div>
                    </div>
                    <div className="db-summary-stats">
                        <div className="db-stat db-stat--points">
                            <span className="db-stat-value">{weeklyStats.wtdPoints}</span>
                            <span className="db-stat-label">SP this week</span>
                        </div>
                        <div className="db-stat">
                            <span className="db-stat-value">{weeklyStats.wtdCount}</span>
                            <span className="db-stat-label">Activities this week</span>
                        </div>
                        <div className="db-stat">
                            <span className="db-stat-value">{weeklyStats.lwPoints}</span>
                            <span className="db-stat-label">SP last week</span>
                        </div>
                        <div className="db-stat db-stat--streak">
                            <span className="db-stat-value">{streak !== null ? streak : '—'}</span>
                            <span className="db-stat-label">Day streak</span>
                        </div>
                    </div>
                </div>

                <div className="db-content">
                    {activeChallenges.length > 0 && (
                        <div className="db-section">
                            <div className="db-section-head">
                                <p className="db-section-title">Active challenges</p>
                                <button
                                    type="button"
                                    className="db-see-all"
                                    onClick={() => history.push('/challenges')}
                                >
                                    See all
                                </button>
                            </div>
                            <div className="db-list">
                                {activeChallenges.map(c => (
                                    <button
                                        type="button"
                                        key={c.challenge_id}
                                        className="db-challenge-card"
                                        onClick={() => history.push(`/challenges/${c.challenge_id}`)}
                                    >
                                        <span className="db-challenge-icon" aria-hidden="true">
                                            {c.icon || '🏅'}
                                        </span>
                                        <span className="db-challenge-body">
                                            <span className="db-challenge-name">{c.name}</span>
                                            <span className="db-challenge-meta">
                                                {c.computed_status === 'upcoming' ? 'Starts soon' : `Week ${c.current_recurrence}/${c.total_runs}`}
                                                {' · '}
                                                {c.participant_count} {c.participant_count === 1 ? 'member' : 'members'}
                                            </span>
                                        </span>
                                        <span className={`db-challenge-role ${c.user_role}`}>
                                            {c.user_role === 'admin' ? 'Admin' : 'Member'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="db-section">
                        <div className="db-section-head">
                            <p className="db-section-title">Activity</p>
                            {selectedActivityTypes.length > 0 ? (
                                <span className="db-filter-hint">{selectedActivityTypes.length} selected</span>
                            ) : (
                                !loading && <span className="db-filter-hint">{filteredActivities.length}</span>
                            )}
                        </div>

                        {error && (
                            <div className="sc-alert sc-alert--error db-alert">{error}</div>
                        )}

                        {Object.keys(stats.activityTypes).length > 0 && (
                            <div className="db-chips">
                                {Object.entries(stats.activityTypes).map(([activity, count]) => {
                                    const selected = selectedActivityTypes.includes(activity);
                                    const label = stats.activityNames?.[activity] || formatActivityName(activity);
                                    return (
                                        <button
                                            key={activity}
                                            type="button"
                                            className={`db-chip ${selected ? 'is-active' : ''}`}
                                            onClick={() => handleActivityTypeClick(activity)}
                                            aria-pressed={selected}
                                        >
                                            <span className="db-chip-name">{label}</span>
                                            <span className="db-chip-count">{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {selectedActivityTypes.length > 0 && (
                            <div className="db-filter-controls">
                                <span className="db-filter-label">Active</span>
                                <div className="db-filters-list">
                                    {selectedActivityTypes.map(type => (
                                        <div key={type} className="db-filter-chip">
                                            <span className="db-chip-value">{stats.activityNames?.[type] || formatActivityName(type)}</span>
                                            <button
                                                type="button"
                                                className="db-chip-remove"
                                                onClick={() => handleActivityTypeClick(type)}
                                                aria-label={`Remove ${stats.activityNames?.[type] || formatActivityName(type)} filter`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" className="db-clear-all" onClick={clearAllFilters}>
                                        Clear all
                                    </button>
                                </div>
                            </div>
                        )}

                        {loading ? (
                            <div className="db-loading">
                                <div className="db-spinner" />
                                <p>Loading activity feed...</p>
                            </div>
                        ) : filteredActivities.length === 0 ? (
                            <p className="db-empty">
                                {selectedActivityTypes.length > 0
                                    ? 'No activities match your filters.'
                                    : 'Log an activity or add friends to see their activities here.'}
                            </p>
                        ) : (
                            <div className="db-feed-list">
                                {filteredActivities.map((activity, index) => (
                                    <article
                                        key={activity.id != null ? `${activity.id}-${index}` : index}
                                        className="db-feed-card"
                                        onClick={() => handleActivityClick(activity.id)}
                                    >
                                        <div className="db-feed-main">
                                            <button
                                                type="button"
                                                className="db-feed-user"
                                                onClick={(e) => openUserProfile(e, activity.username)}
                                                aria-label={activity.username ? `View ${activity.username}'s profile` : 'View profile'}
                                            >
                                                {activity.avatar_id && avatars[activity.avatar_id] ? (
                                                    <Avatar
                                                        svgContent={avatars[activity.avatar_id]}
                                                        alt=""
                                                        className="db-feed-avatar"
                                                    />
                                                ) : (
                                                    <span className="db-feed-avatar db-feed-avatar--empty" aria-hidden="true" />
                                                )}
                                            </button>
                                            <div className="db-feed-body">
                                                <button
                                                    type="button"
                                                    className="db-feed-type"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleActivityClick(activity.id);
                                                    }}
                                                >
                                                    {activity.activity_name || formatActivityName(activity.activity)}
                                                </button>
                                                <div className="db-feed-meta">
                                                    <button
                                                        type="button"
                                                        className="db-feed-username"
                                                        onClick={(e) => openUserProfile(e, activity.username)}
                                                    >
                                                        {activity.username || 'Unknown'}
                                                        {activity.is_own && <span className="db-you-badge">You</span>}
                                                    </button>
                                                    <span>
                                                        {new Date(activity.date + 'T00:00').toLocaleDateString()}
                                                    </span>
                                                    {activity.time != null && activity.time > 0 && (
                                                        <span>{Math.round(activity.time)} min</span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="db-feed-points">{activity.points ?? 0} SP</span>
                                        </div>
                                        <ReactionBar
                                            activityId={activity.id}
                                            reactions={reactions[activity.id] || []}
                                            currentUserId={currentUserId}
                                            onReactionChange={handleReactionChange}
                                            allowedEmojis={allowedEmojis}
                                        />
                                    </article>
                                ))}

                                <div ref={sentinelRef} className="db-sentinel">
                                    {loadingMore && (
                                        <div className="db-loading-more">
                                            <div className="db-spinner-small" />
                                            <p>Loading more...</p>
                                        </div>
                                    )}
                                    {!hasMore && activities.length > 0 && (
                                        <p className="db-feed-end">You&apos;re all caught up</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
